import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, type Ref, toValue } from 'vue';
import { http } from '@/axios/instance/http';
import type { ConfigApiUserRole, Cycle } from '@/ccp-mfe/types';
import type { EmployeeResponse } from '@/ccp-mfe/types/ccp-details';

// Types
export type CycleInfoQueryKeyVars = {
  cycleId: number;
  role: ConfigApiUserRole;
};

// Cycle info fetcher
export const fetchCycleInfo = async (vars: { cycleId: number; role: ConfigApiUserRole }) => {
  const res = await http.get<Cycle>(`v2/ccp/cycles/${vars.cycleId}`, { params: { role: vars.role } });

  return res;
};

// Fetches the cycle info
export const useCycleInfoQuery = (
  vars: Ref<CycleInfoQueryKeyVars>,
  options: { enabled: MaybeRef<boolean> } = { enabled: true }
) => {
  return useQuery({
    queryKey: ['cycleInfo', vars],
    queryFn: async () => {
      const { cycleId, role } = toValue(vars);

      const res = await fetchCycleInfo({ cycleId, role: role || 'admin' });

      return res.data;
    },
    enabled: options.enabled,
  });
};

// Fetches the employees for settings import to check if the cycle has employees
export const useCcpSettingsEmployeesQuery = (vars: Ref<{ cycleId: number }>) => {
  return useQuery({
    queryKey: ['ccpSettingsEmployees', vars],
    queryFn: async () => {
      const res = await http.post<EmployeeResponse>(
        `v2/ccp/cycles/${vars.value.cycleId}/employees/filtered`,
        {},
        {
          params: { role: 'admin' },
        }
      );

      return res.data;
    },
  });
};

// Ends the cycle
export const useEndCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.put<string>(`v2/ccp/cycles/${vars.cycleId}/end_cycle`),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
    },
  });
};

// Archives the cycle
export const useArchiveCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number; revokeAccess: boolean }) =>
      http.put(`v2/ccp/cycles/${vars.cycleId}/archive_cycle`, { revoke_access: vars.revokeAccess }),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });
};

// Ends and archives the cycle
export const useEndAndArchiveCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { cycleId: number; revokeAccess: boolean }) => {
      await http.put<string>(`v2/ccp/cycles/${vars.cycleId}/end_cycle`);
      await http.put(`v2/ccp/cycles/${vars.cycleId}/archive_cycle`, { revoke_access: vars.revokeAccess });
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });
};

// Unarchives the cycle
export const useUnarchiveCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.put<string>(`v2/ccp/cycles/${vars.cycleId}/unarchive_cycle`),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });
};

// Exports cycle data to HRIS provider
export const useExportToHrisMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.post(`v2/ccp/cycles/${vars.cycleId}/export-to-hris`),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
    },
  });
};

// Deletes the cycle (completely removes it)
export const useDeleteCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.delete(`v2/ccp/cycles/${vars.cycleId}/`),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
    },
  });
};
